import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import { buildReportLabels } from '../../utils/reportLabels';
import DataTable from '../../components/ui/DataTable';
import ReportExportButtons from '../../components/ui/ReportExportButtons';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import PersonCell from '../../components/ui/PersonCell';
import { dateFilterColumn } from '../../utils/dateFilterColumn';
import { useTaFilterColumns } from '../pdks-reports/config/taFilterColumns';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const MOVEMENT_EXPORT_URL = '/v1/movement-records/export';

const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Nunito Sans', sans-serif;
  color: #fff;
  background: ${({ $type }) =>
    $type === 'entry' ? '#22c55e'
      : $type === 'exit' ? '#ef4444'
      : '#0291EF'};
`;

export default function MovementRecordsPage() {
  const { t, i18n } = useTranslation();
  const { canWrite } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [gates, setGates] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [] });
  const [deleting, setDeleting] = useState(false);
  const lastValidQueryParams = useRef(null);

  // AKM POC §7.6 — sayfa AC.Read ile açılır; manuel kayıt ve silme AC.Write ister. Kapı listesi yalnız manuel kayıt
  // çekmecesini besler — yazma yetkisi yoksa çekilmez.
  const canWriteAc = canWrite('AC');
  useEffect(() => {
    if (!canWriteAc) return;
    api.get('/v1/gates?pageSize=500').then((res) => {
      setGates(res.data?.data?.data || res.data?.data || []);
    }).catch(() => {});
  }, [canWriteAc]);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get('/v1/movement-records', { params: paramsToUse })
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const handleNewButtonClick = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    try {
      await api.post('/v1/movement-records/manual', formData);
      setRefreshTrigger((p) => p + 1);
      setDrawerOpen(false);
      return true;
    } catch {
      return false;
    }
  }, []);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id] });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      for (const id of deleteConfirm.ids) {
        await api.delete(`/v1/movement-records/${id}`);
      }
      setRefreshTrigger((p) => p + 1);
    } catch { /* interceptor */ }
    setDeleteConfirm({ isOpen: false, ids: [] });
    setDeleting(false);
  }, [deleteConfirm.ids]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const directionLabel = useCallback((val) => {
    const map = {
      entry: t('gate.entry'),
      exit: t('gate.exit'),
      bidirectional: t('gate.bidirectional'),
    };
    return map[val] || val;
  }, [t]);

  const sourceLabel = useCallback((val) => {
    const map = {
      qr: t('movementRecord.qr'),
      qr_scan: t('movementRecord.qr'),
      manual: t('movementRecord.manualSource'),
      card: t('movementRecord.card'),
      api: t('movementRecord.apiSource'),
    };
    return map[val] || val;
  }, [t]);

  const directionFilterOptions = useMemo(() => [
    { id: 'entry', name: t('gate.entry') },
    { id: 'exit', name: t('gate.exit') },
  ], [t]);

  const sourceFilterOptions = useMemo(() => [
    { id: 'qr', name: t('movementRecord.qr') },
    { id: 'qr_scan', name: t('movementRecord.qr') },
    { id: 'manual', name: t('movementRecord.manualSource') },
    { id: 'card', name: t('movementRecord.card') },
    { id: 'api', name: t('movementRecord.apiSource') },
  ], [t]);

  const actionOptions = useMemo(() => {
    if (!canWriteAc) return [];
    return [
      { type: 'delete', label: t('common.delete'), onClick: handleDelete },
    ];
  }, [t, canWriteAc, handleDelete]);

  // Ortak süzgeç setinden yalnız departman (birincil pozisyon) ve lokasyon — hareket ucu bu ikisini alır.
  const filterColumns = useTaFilterColumns(t, ['departmentId', 'locationId']);

  const baseColumns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'userFullName', label: t('movementRecord.userFullName'),
      options: {
        filter: true, sort: false,
        filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <PersonCell name={value} profilePicture={row?.userProfilePicture} />;
        },
      },
    },
    {
      name: 'buildingName', label: t('movementRecord.buildingName'),
      options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'gateName', label: t('movementRecord.gateName'),
      options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      // AKM POC §7.6 — okuyucu (turnike / kart okuyucu): kapının hangi okuyucusundan geçildi.
      name: 'accessPointName', label: t('movementRecord.accessPoint'),
      options: {
        filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (v) => v || '-',
      },
    },
    {
      name: 'direction', label: t('movementRecord.direction'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          names: directionFilterOptions.map((o) => o.id),
          defaultOp: 'eq',
          renderValue: (v) => directionFilterOptions.find((o) => o.id === v)?.name || v,
        },
        customFilterListOptions: { render: (v) => `${t('movementRecord.direction')}: ${directionFilterOptions.find((o) => o.id === v)?.name || v}` },
        customBodyRender: (value) => (
          <Badge $type={value}>{directionLabel(value)}</Badge>
        ),
      },
    },
    dateFilterColumn('timestamp', t('movementRecord.timestamp'), i18n.language, { includeTime: true }),
    {
      name: 'source', label: t('movementRecord.source'),
      options: {
        filter: true, sort: false,
        display: 'false', download: false,
        filterType: 'dropdown',
        filterOptions: {
          names: sourceFilterOptions.map((o) => o.id),
          defaultOp: 'eq',
          renderValue: (v) => sourceFilterOptions.find((o) => o.id === v)?.name || v,
        },
        customFilterListOptions: { render: (v) => `${t('movementRecord.source')}: ${sourceFilterOptions.find((o) => o.id === v)?.name || v}` },
        customBodyRender: (value) => sourceLabel(value),
      },
    },
    ...filterColumns,
  ], [t, i18n.language, data, directionLabel, sourceLabel, directionFilterOptions, sourceFilterOptions, filterColumns]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, columns);
    setQueryParams(params);
  }, [columns]);

  // AKM POC §7.1 — sunucu tarafı Excel / PDF (TAM veri, o anki süzgeçler); istemci tarafı "yalnız ekrandaki sayfa" CSV'si
  // kalktı. Yön ve kaynak adları web dilinde gider.
  const getExportLabels = useCallback(() => buildReportLabels(t, baseColumns, t('movementRecord.title'), {
    'direction.entry': directionLabel('entry'),
    'direction.exit': directionLabel('exit'),
    'direction.bidirectional': directionLabel('bidirectional'),
    'source.qr': sourceLabel('qr'),
    'source.manual': sourceLabel('manual'),
    'source.card': sourceLabel('card'),
    'source.api': sourceLabel('api'),
  }), [t, baseColumns, directionLabel, sourceLabel]);

  const toolbarActions = useMemo(() => (
    <ReportExportButtons url={MOVEMENT_EXPORT_URL} params={queryParams} getLabels={getExportLabels} />
  ), [queryParams, getExportLabels]);

  const directionOptions = useMemo(() => [
    { id: 'entry', name: t('gate.entry') },
    { id: 'exit', name: t('gate.exit') },
  ], [t]);

  const searchUsers = useCallback(async (query) => {
    const params = query ? { 'firstName[like]': query, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    const result = res.data?.data;
    const items = Array.isArray(result) ? result : (result?.data || []);
    return items.map((u) => ({
      id: u.userId || u.id,
      name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
      profilePicture: u.profilePicture,
    }));
  }, []);

  const objectSchema = useMemo(() => [
    {
      name: 'userId', label: t('movementRecord.userFullName'), type: 'asyncSelect', grid: 12, required: true,
      onSearch: searchUsers,
      renderOption: (o) => <PersonCell name={o.name} profilePicture={o.profilePicture} />,
    },
    {
      name: 'gateId', label: t('movementRecord.gateName'), type: 'select', grid: 12, required: true,
      options: gates, idKey: 'id', labelKey: 'name',
    },
    {
      name: 'direction', label: t('movementRecord.direction'), type: 'select', grid: 12, required: true,
      options: directionOptions, idKey: 'id', labelKey: 'name',
    },
    { name: 'timestamp', label: t('movementRecord.timestamp'), type: 'datetime', grid: 12 },
    // Genel ekranda opsiyonel gerekçe; Eksik Kayıtlar akışında (MissingRecordsPage) zorunlu.
    { name: 'note', label: t('movementRecord.note'), type: 'textarea', grid: 12 },
  ], [t, gates, directionOptions, searchUsers]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('movementRecord.deleteTitle')}
        message={t('movementRecord.deleteMessage', { count: deleteConfirm.ids.length })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [] })}
        loading={deleting}
      />

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('movementRecord.manual')}
        schema={objectSchema}
        onSave={handleSave}
      />

      {useMemo(() => (
        <DataTable
          title={t('movementRecord.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWriteAc}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWriteAc ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          toolbarActions={toolbarActions}
          download={false}
        />
      ), [
        t, fetching, columns, data, pagination, canWriteAc,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange, toolbarActions,
      ])}
    </Container>
  );
}
